import { useState } from "react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ChevronLeft, ChevronRight, Check, User, Heart, Beaker, Package, FileCheck, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { api, Patient } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface OrderEntryWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  editOrder?: any;
  isReviewMode?: boolean;
  onApproveOrder?: (orderId: string) => Promise<void>;
}

interface OrderFormData {
  // Step 1: Patient (Recipient) Information
  mrn: string;
  patientId?: string;
  firstName: string;
  lastName: string;
  middleName: string;
  dateOfBirth: string;
  gender: string;
  weight: string;
  diagnosisStage: string;
  aboRh: string;
  ssn: string;
  physician: string;
  contactPerson: string;
  phoneNumber: string;
  
  // Step 2: Donor Information
  donorNotApplicable: boolean;
  donorFirstName: string;
  donorLastName: string;
  donorMiddleName: string;
  donorGender: string;
  donorWeight: string;
  donorDob: string;
  donorMrn: string;
  donorAboRh: string;
  
  // Step 3: Processing Requirements
  collectionDate: string;
  processingWithin24h: boolean;
  processingWithin48h: boolean;
  hpcApheresisProc: string[];
  tcTCellsProc: string[];
  hpcMarrowProc: string[];
  hpcCordBloodProc: string[];
  commonAssaysProc: string[];
  
  // Step 4: Packaging
  specialAliquoting: boolean;
  cd34DoseOption: string;
  cd34SplitEvenly: boolean;
  cd34MinDose: string;
  cd34MaxDose: string;
  cd34BagDoses: { [key: string]: string };
  cd3DoseOption: string;
  cd3SplitEvenly: boolean;
  cd3BagDoses: { [key: string]: string };
  additionalPackagingInstructions: string;
  
  // Step 5: Miscellaneous
  intendedTncDose: string;
  intendedCd34Dose: string;
  intendedCd3Dose: string;
  aboRhTestingOption: string;
  physicianApproval: boolean;
  // Missing fields added
  cellDose: string;
  cellDoseUnit: string;
  infusionDate: string;
  treatmentType: string;
  specialInstructions: string;
}

export function OrderEntryWizard({ isOpen, onClose, onComplete, editOrder, isReviewMode = false, onApproveOrder }: OrderEntryWizardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingPatient, setExistingPatient] = useState<Patient | null>(null);
  const [validationError, setValidationError] = useState<string>("");
  const [diagnosisSuggestions, setDiagnosisSuggestions] = useState<any[]>([]);
  const [showDiagnosisSuggestions, setShowDiagnosisSuggestions] = useState(false);

  // Check if current user can edit this order
  const canUserEditOrder = () => {
    if (!user || !editOrder) return true; // Allow new orders
    
    const userRole = user.role;
    const orderStatus = editOrder.order_status;
    
    // Clinicians can only edit orders with 'unapproved' status or no status
    if (userRole === 'clinician') {
      return orderStatus === 'unapproved' || orderStatus === null || orderStatus === undefined;
    }
    
    // Physicians can edit unapproved orders
    if (userRole === 'physician') {
      return orderStatus === 'unapproved';
    }
    
    // Supervisors and admins can edit any status
    return userRole === 'supervisor' || userRole === 'admin';
  };

  // Check permissions when component mounts or editOrder changes
  React.useEffect(() => {
    if (isOpen && editOrder && !canUserEditOrder() && !isReviewMode) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit this order",
        variant: "destructive"
      });
      onClose();
      return;
    }
  }, [isOpen, editOrder, user]);

  // Determine if fields should be disabled based on user role and order status
  const shouldDisableFields = () => {
    // If order is approved/in-progress, disable all fields for everyone
    if (editOrder && (editOrder.order_status === 'approved' || editOrder.order_status === 'in_progress')) {
      return true;
    }
    
    // In review mode, only physicians can edit (they can approve)
    if (isReviewMode && user?.role !== 'physician') {
      return true;
    }
    
    // Supervisors in view mode cannot edit
    if (user?.role === 'supervisor' && !isReviewMode) {
      return true;
    }
    
    // For unapproved orders, clinicians and physicians can edit all fields
    if (editOrder && editOrder.order_status === 'unapproved' && 
        (user?.role === 'clinician' || user?.role === 'physician')) {
      return false;
    }
    
    // Unauthorized editing
    if (editOrder && !canUserEditOrder() && !isReviewMode) {
      return true;
    }
    
    return false;
  };

  // Initialize form data with proper reset logic - moved before conditional return
  const getInitialFormData = (): OrderFormData => {
    if (editOrder) {
      return {
        // Patient Information - Access from nested patients object
        mrn: editOrder?.patients?.mrn || "",
        patientId: editOrder?.patient_id || "",
        firstName: editOrder?.patients?.first_name || "",
        lastName: editOrder?.patients?.last_name || "",
        middleName: editOrder?.patients?.middle_name || "",
        dateOfBirth: editOrder?.patients?.date_of_birth || "",
        gender: editOrder?.patients?.gender || "",
        weight: editOrder?.patients?.weight_kg?.toString() || "",
        diagnosisStage: editOrder?.diagnosis || "",
        aboRh: editOrder?.patients?.abo_rh || "",
        ssn: editOrder?.patients?.ssn || "",
        physician: editOrder?.patients?.physician || "",
        contactPerson: editOrder?.patients?.emergency_contact_name || "",
        phoneNumber: editOrder?.patients?.emergency_contact_phone || "",
        
        // ... keep existing code (all other fields from editOrder)
        donorNotApplicable: editOrder?.donor_not_applicable || false,
        donorFirstName: editOrder?.donor_first_name || "",
        donorLastName: editOrder?.donor_last_name || "",
        donorMiddleName: editOrder?.donor_middle_name || "",
        donorGender: editOrder?.donor_gender || "",
        donorWeight: editOrder?.donor_weight_kg?.toString() || "",
        donorDob: editOrder?.donor_dob || "",
        donorMrn: editOrder?.donor_mrn || "",
        donorAboRh: editOrder?.donor_abo_rh || "",
        
        // Processing Requirements
        collectionDate: editOrder?.collection_date || "",
        processingWithin24h: editOrder?.processing_within_24h || false,
        processingWithin48h: editOrder?.processing_within_48h || false,
        hpcApheresisProc: editOrder?.hpc_apheresis_procedures || [],
        tcTCellsProc: editOrder?.tc_t_cells_procedures || [],
        hpcMarrowProc: editOrder?.hpc_marrow_procedures || [],
        hpcCordBloodProc: editOrder?.hpc_cord_blood_procedures || [],
        commonAssaysProc: editOrder?.common_assays_procedures || [],
        
        // Packaging
        specialAliquoting: editOrder?.special_aliquoting || false,
        cd34DoseOption: editOrder?.cd34_dose_option || "",
        cd34SplitEvenly: editOrder?.cd34_split_evenly || false,
        cd34MinDose: editOrder?.cd34_min_dose?.toString() || "",
        cd34MaxDose: editOrder?.cd34_max_dose?.toString() || "",
        cd34BagDoses: editOrder?.cd34_bag_doses || {},
        cd3DoseOption: editOrder?.cd3_dose_option || "",
        cd3SplitEvenly: editOrder?.cd3_split_evenly || false,
        cd3BagDoses: editOrder?.cd3_bag_doses || {},
        additionalPackagingInstructions: editOrder?.additional_packaging_instructions || "",
        
        // Miscellaneous
        intendedTncDose: editOrder?.intended_tnc_dose?.toString() || "",
        intendedCd34Dose: editOrder?.intended_cd34_dose?.toString() || "",
        intendedCd3Dose: editOrder?.intended_cd3_dose?.toString() || "",
        aboRhTestingOption: editOrder?.abo_rh_testing_option || "",
        physicianApproval: editOrder?.physician_approval || false,
        // Missing fields
        cellDose: editOrder?.cell_dose?.toString() || "",
        cellDoseUnit: editOrder?.cell_dose_unit || "",
        infusionDate: editOrder?.infusion_date || "",
        treatmentType: editOrder?.treatment_type || "",
        specialInstructions: editOrder?.special_instructions || "",
      };
    } else {
      // Return completely fresh/empty form data for new orders
      return {
        // Patient Information
        mrn: "",
        patientId: "",
        firstName: "",
        lastName: "",
        middleName: "",
        dateOfBirth: "",
        gender: "",
        weight: "",
        diagnosisStage: "",
        aboRh: "",
        ssn: "",
        physician: "",
        contactPerson: "",
        phoneNumber: "",
        
        // Donor Information
        donorNotApplicable: false,
        donorFirstName: "",
        donorLastName: "",
        donorMiddleName: "",
        donorGender: "",
        donorWeight: "",
        donorDob: "",
        donorMrn: "",
        donorAboRh: "",
        
        // Processing Requirements
        collectionDate: "",
        processingWithin24h: false,
        processingWithin48h: false,
        hpcApheresisProc: [],
        tcTCellsProc: [],
        hpcMarrowProc: [],
        hpcCordBloodProc: [],
        commonAssaysProc: [],
        
        // Packaging
        specialAliquoting: false,
        cd34DoseOption: "",
        cd34SplitEvenly: false,
        cd34MinDose: "",
        cd34MaxDose: "",
        cd34BagDoses: {},
        cd3DoseOption: "",
        cd3SplitEvenly: false,
        cd3BagDoses: {},
        additionalPackagingInstructions: "",
        
        // Miscellaneous
        intendedTncDose: "",
        intendedCd34Dose: "",
        intendedCd3Dose: "",
        aboRhTestingOption: "",
        physicianApproval: false,
        // Missing fields
        cellDose: "",
        cellDoseUnit: "",
        infusionDate: "",
        treatmentType: "",
        specialInstructions: "",
      };
    }
  };

  const [formData, setFormData] = useState<OrderFormData>(getInitialFormData());

  // Reset form when dialog opens/closes or when editOrder changes
  React.useEffect(() => {
    if (isOpen) {
      const loadFormData = async () => {
        if (editOrder && editOrder.patient_id) {
          // When editing, use the patient data already included in editOrder
          console.log("Loading edit order data:", editOrder);
          
          // Set existing patient from the nested patients data
          if (editOrder.patients) {
            console.log("Setting existing patient:", editOrder.patients);
            setExistingPatient(editOrder.patients);
          } else {
            console.log("No nested patients data found, setting patientId only");
            setExistingPatient({ id: editOrder.patient_id } as any);
          }
          
          // Load form data directly from editOrder (no additional API call needed)
          const initialData = getInitialFormData();
          console.log("Setting form data for edit:", initialData);
          setFormData(initialData);
        } else {
          // New order - use empty form
          console.log("Loading new order form");
          const initialData = getInitialFormData();
          setFormData(initialData);
          setExistingPatient(null);
        }
      };
      
      loadFormData();
      setCurrentStep(1);
    }
  }, [isOpen, editOrder]);

  // Don't render anything if not open to prevent validation loops
  if (!isOpen) {
    return null;
  }

  const steps = [
    { number: 1, title: "Patient Info", icon: User, description: "Recipient information and demographics" },
    { number: 2, title: "Donor Info", icon: Heart, description: "Donor information (if applicable)" },
    { number: 3, title: "Processing", icon: Beaker, description: "Processing requirements and procedures" },
    { number: 4, title: "Packaging", icon: Package, description: "Aliquoting and packaging specifications" },
    { number: 5, title: "Miscellaneous", icon: FileCheck, description: "Collection goals and final approval" },
  ];

  const handleInputChange = (field: keyof OrderFormData, value: string | boolean | string[]) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Update validation error dynamically as user types for step 1
      if (currentStep === 1 && validationError) {
        // Check validation with the new data
        const missingFields = [];
        if (!newData.lastName?.trim()) missingFields.push("Last Name");
        if (!newData.firstName?.trim()) missingFields.push("First Name");
        if (newData.mrn?.length !== 7) missingFields.push("MRN (must be 7 digits)");
        if (!newData.dateOfBirth?.trim()) missingFields.push("Date of Birth");
        if (!newData.gender?.trim()) missingFields.push("Sex");
        if (!newData.weight?.trim()) missingFields.push("Weight");
        if (!newData.diagnosisStage?.trim()) missingFields.push("Diagnosis/Stage");
        if (!newData.physician?.trim()) missingFields.push("Physician");
        if (!newData.contactPerson?.trim()) missingFields.push("Contact Person");
        if (!newData.phoneNumber?.trim()) missingFields.push("Phone Number");
        
        const newErrorMessage = missingFields.length === 0 ? "" : `Please fill in the following required fields: ${missingFields.join(", ")}`;
        if (newErrorMessage !== validationError) {
          setValidationError(newErrorMessage);
        }
      }
      
      return newData;
    });
    
    // If physician approval changes, clear any validation error to trigger re-render
    if (field === 'physicianApproval') {
      setValidationError("");
    }
  };

  const handleBagDoseChange = (field: 'cd34BagDoses' | 'cd3BagDoses', bagDoses: { [key: string]: string }) => {
    setFormData(prev => ({ ...prev, [field]: bagDoses }));
  };

  // Helper function to create valid HTML IDs from procedure names
  const createValidId = (text: string, prefix: string = '', index: number = 0): string => {
    return `${prefix}-${text.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${index}`;
  };

  // Blood group options
  const bloodGroups = [
    "A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"
  ];

  // ICD API search function
  const searchICD = async (query: string) => {
    if (query.length < 2) {
      setDiagnosisSuggestions([]);
      setShowDiagnosisSuggestions(false);
      return;
    }

    try {
      // Using ICD API for diagnosis lookup
      const response = await fetch(`https://icd-api.com/api/icd10?text=${encodeURIComponent(query)}&format=json`);
      if (response.ok) {
        const data = await response.json();
        setDiagnosisSuggestions(data.slice(0, 5)); // Show top 5 results
        setShowDiagnosisSuggestions(true);
      }
    } catch (error) {
      console.error("ICD API error:", error);
      // Fallback to static suggestions if API fails
      const staticSuggestions = [
        { code: "C92.00", description: "Acute myeloblastic leukemia, not having achieved remission" },
        { code: "C92.10", description: "Chronic myeloid leukemia, BCR/ABL-positive" },
        { code: "C91.00", description: "Acute lymphoblastic leukemia, not having achieved remission" },
        { code: "D46.9", description: "Myelodysplastic syndrome, unspecified" },
        { code: "C96.0", description: "Multifocal and multisystemic Langerhans-cell histiocytosis" }
      ].filter(item => item.description.toLowerCase().includes(query.toLowerCase()));
      
      setDiagnosisSuggestions(staticSuggestions);
      setShowDiagnosisSuggestions(staticSuggestions.length > 0);
    }
  };

  const handleArrayChange = (field: keyof OrderFormData, value: string, checked: boolean) => {
    setFormData(prev => {
      const currentArray = (prev[field] as string[]) || [];
      if (checked) {
        return { ...prev, [field]: [...currentArray, value] };
      } else {
        return { ...prev, [field]: currentArray.filter(item => item !== value) };
      }
    });
  };

  const searchPatientByMRN = async (mrn: string) => {
    if (mrn.length === 7) {
      try {
        console.log("Searching for patient with MRN:", mrn);
        const patient = await api.getPatientByMRN(mrn);
        if (patient) {
          console.log("Found patient:", patient);
          setExistingPatient(patient);
          const updatedFormData = {
            ...formData,
            patientId: patient.id,
            firstName: patient.first_name,
            lastName: patient.last_name,
            middleName: patient.middle_name || "",
            dateOfBirth: patient.date_of_birth,
            gender: patient.gender,
            weight: patient.weight_kg?.toString() || "",
            aboRh: patient.abo_rh || "",
            ssn: patient.ssn || "",
            physician: patient.physician || "",
            contactPerson: patient.emergency_contact_name || "",
            phoneNumber: patient.emergency_contact_phone || "",
          };
          console.log("Updating form data:", updatedFormData);
          setFormData(updatedFormData);
        } else {
          console.log("No patient found with MRN:", mrn);
          setExistingPatient(null);
        }
      } catch (error) {
        console.error("Error searching patient:", error);
        setExistingPatient(null);
      }
    } else if (mrn.length === 0) {
      // Clear existing patient when MRN is cleared
      setExistingPatient(null);
    }
  };

  const validateStep = (step: number): boolean => {
    // Supervisors can navigate through all steps as they're read-only
    if (user?.role === 'supervisor') {
      return true;
    }

    switch (step) {
      case 1:
        // Required: last name, first name, mrn, dob, sex, weight, diagnosis, physician, contact person, phone number
        const validationChecks = {
          lastName: {
            value: formData.lastName,
            valid: !!formData.lastName?.trim(),
            reason: formData.lastName?.trim() ? 'OK' : 'MISSING'
          },
          firstName: {
            value: formData.firstName,
            valid: !!formData.firstName?.trim(),
            reason: formData.firstName?.trim() ? 'OK' : 'MISSING'
          },
          mrn: {
            value: formData.mrn,
            length: formData.mrn?.length,
            valid: formData.mrn?.length === 7,
            reason: formData.mrn?.length === 7 ? 'OK' : `INVALID - Need 7 digits, got ${formData.mrn?.length || 0}`
          },
          dateOfBirth: {
            value: formData.dateOfBirth,
            valid: !!formData.dateOfBirth?.trim(),
            reason: formData.dateOfBirth?.trim() ? 'OK' : 'MISSING'
          },
          gender: {
            value: formData.gender,
            valid: !!formData.gender?.trim(),
            reason: formData.gender?.trim() ? 'OK' : 'MISSING'
          },
          weight: {
            value: formData.weight,
            valid: !!formData.weight?.trim(),
            reason: formData.weight?.trim() ? 'OK' : 'MISSING'
          },
          diagnosisStage: {
            value: formData.diagnosisStage,
            valid: !!formData.diagnosisStage?.trim(),
            reason: formData.diagnosisStage?.trim() ? 'OK' : 'MISSING'
          },
          physician: {
            value: formData.physician,
            valid: !!formData.physician?.trim(),
            reason: formData.physician?.trim() ? 'OK' : 'MISSING'
          },
          contactPerson: {
            value: formData.contactPerson,
            valid: !!formData.contactPerson?.trim(),
            reason: formData.contactPerson?.trim() ? 'OK' : 'MISSING'
          },
          phoneNumber: {
            value: formData.phoneNumber,
            valid: !!formData.phoneNumber?.trim(),
            reason: formData.phoneNumber?.trim() ? 'OK' : 'MISSING'
          }
        };

        const step1Valid = !!(
          formData.lastName?.trim() &&
          formData.firstName?.trim() &&
          formData.mrn?.length === 7 &&
          formData.dateOfBirth?.trim() &&
          formData.gender?.trim() &&
          formData.weight?.trim() &&
          formData.diagnosisStage?.trim() &&
          formData.physician?.trim() &&
          formData.contactPerson?.trim() &&
          formData.phoneNumber?.trim()
        );
        
        return step1Valid;
      case 2:
        return true; // All optional
      case 3:
        // At least one processing should be checked
        return !!(
          formData.hpcApheresisProc.length > 0 ||
          formData.tcTCellsProc.length > 0 ||
          formData.hpcMarrowProc.length > 0 ||
          formData.hpcCordBloodProc.length > 0 ||
          formData.commonAssaysProc.length > 0
        );
      case 4:
        // If special aliquoting is yes, then user must enter some value below
        if (formData.specialAliquoting) {
          return !!(formData.cd34DoseOption || formData.cd3DoseOption || formData.additionalPackagingInstructions?.trim());
        }
        return true; // If not selected, it's valid
      case 5:
        // Step 5 validation - check required fields for physicians
        if (isReviewMode) {
          // For physician approval, check that approval is given AND required fields are complete
          const hasAboRhSelection = !!formData.aboRhTestingOption?.trim();
          return formData.physicianApproval && hasAboRhSelection;
        } else {
          // For regular users, ABO/Rh testing option is required
          return !!formData.aboRhTestingOption?.trim();
        }
      default:
        return false;
    }
  };

  const nextStep = () => {
    const isValid = validateStep(currentStep);
    
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      setValidationError("");
    } else {
      setValidationError(getValidationErrorMessage(currentStep));
    }
  };

  const getValidationErrorMessage = (step: number): string => {
    switch (step) {
      case 1:
        const missingFields = [];
        if (!formData.lastName?.trim()) missingFields.push("Last Name");
        if (!formData.firstName?.trim()) missingFields.push("First Name");
        if (formData.mrn?.length !== 7) missingFields.push("MRN (must be 7 digits)");
        if (!formData.dateOfBirth?.trim()) missingFields.push("Date of Birth");
        if (!formData.gender?.trim()) missingFields.push("Sex");
        if (!formData.weight?.trim()) missingFields.push("Weight");
        if (!formData.diagnosisStage?.trim()) missingFields.push("Diagnosis/Stage");
        if (!formData.physician?.trim()) missingFields.push("Physician");
        if (!formData.contactPerson?.trim()) missingFields.push("Contact Person");
        if (!formData.phoneNumber?.trim()) missingFields.push("Phone Number");
        
        if (missingFields.length === 0) return "";
        return `Please fill in the following required fields: ${missingFields.join(", ")}`;
      case 3:
        return "Please select at least one processing procedure.";
      case 4:
        return "If special aliquoting is selected, please specify CD34/CD3 dose options or additional instructions.";
      case 5:
        if (isReviewMode) {
          return "Please select an ABO/Rh testing option and check the approval box to proceed.";
        } else {
          return "Please select an ABO/Rh testing option.";
        }
      default:
        return "Please fill in all required fields before continuing.";
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setValidationError("");
  };

  // Helper function to check if all steps are valid
  const areAllStepsValid = () => {
    return steps.every(step => validateStep(step.number));
  };

  // Navigate to specific step (only if all steps are valid)
  const navigateToStep = (stepNumber: number) => {
    if (areAllStepsValid()) {
      setCurrentStep(stepNumber);
      setValidationError("");
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    
    // Additional validation before submitting
    if (!formData.mrn?.trim()) {
      toast({
        title: "Validation Error",
        description: "Patient MRN is required to save the order.",
        variant: "destructive",
      });
      return;
    }
    
    if (!formData.firstName?.trim() || !formData.lastName?.trim()) {
      toast({
        title: "Validation Error", 
        description: "Patient first name and last name are required.",
        variant: "destructive",
      });
      return;
    }
    
    console.log("Starting order submission with MRN:", formData.mrn);
    
    setIsSubmitting(true);
    try {
      if (isReviewMode && editOrder) {
        // This is approval mode - use the hook function for optimistic updates
        console.log('About to approve order in wizard, onApproveOrder exists:', !!onApproveOrder)
        if (onApproveOrder) {
          console.log('Calling onApproveOrder with orderId:', editOrder.id)
          await onApproveOrder(editOrder.id);
        } else {
          console.log('Fallback to direct API call')
          // Fallback to direct API call
          await api.updateOrderStatus(editOrder.id, 'new_order_placed', user?.id);
        }
        toast({
          title: "Order Approved Successfully",
          description: `Facility manipulation order for ${formData.firstName} ${formData.lastName} has been approved.`,
        });
        
        // Close the wizard immediately after approval
        onComplete();
        onClose();
        return; // Exit early to avoid running the create/update logic below
      } else {
        // Create or update patient if needed
        let patientId = formData.patientId;
        
        if (!existingPatient && !patientId) {
          // Create a new patient if no existing patient found and no patientId
          console.log("Creating new patient with data:", {
            mrn: formData.mrn,
            first_name: formData.firstName,
            last_name: formData.lastName,
          });
          
          try {
            const newPatient = await api.createPatient({
              mrn: formData.mrn,
              first_name: formData.firstName,
              last_name: formData.lastName,
              middle_name: formData.middleName,
              date_of_birth: formData.dateOfBirth,
              gender: formData.gender as 'male' | 'female' | 'other',
              weight_kg: parseFloat(formData.weight) || null,
              abo_rh: formData.aboRh,
              ssn: formData.ssn,
              physician: formData.physician,
              emergency_contact_name: formData.contactPerson,
              emergency_contact_phone: formData.phoneNumber,
            });
            patientId = newPatient.id;
            console.log("Successfully created patient with ID:", patientId);
          } catch (error: any) {
            console.error("Error creating patient:", error);
            if (error.message?.includes('409') || error.code === '23505') {
              // Handle unique constraint violation
              toast({
                title: "Patient Creation Error",
                description: `A patient with MRN ${formData.mrn} already exists. Please check the MRN and try again.`,
                variant: "destructive",
              });
            } else {
              toast({
                title: "Error Creating Patient",
                description: "Failed to create patient record. Please try again.",
                variant: "destructive",
              });
            }
            throw error;
          }
        } else if (existingPatient || (editOrder && formData.patientId)) {
          // Update existing patient data when editing an order
          const patientIdToUpdate = existingPatient?.id || formData.patientId;
          console.log("Updating existing patient:", patientIdToUpdate);
          
          if (patientIdToUpdate) {
            try {
              await api.updatePatient(patientIdToUpdate, {
                first_name: formData.firstName,
                last_name: formData.lastName,
                middle_name: formData.middleName,
                weight_kg: parseFloat(formData.weight) || null,
                abo_rh: formData.aboRh,
                physician: formData.physician,
                emergency_contact_name: formData.contactPerson,
                emergency_contact_phone: formData.phoneNumber,
              });
              patientId = patientIdToUpdate;
              console.log("Successfully updated patient:", patientId);
            } catch (error: any) {
              console.error("Error updating patient:", error);
              toast({
                title: "Error Updating Patient",
                description: "Failed to update patient record. Please try again.",
                variant: "destructive",
              });
              throw error;
            }
          } else {
            console.error("No patient ID found for update");
            throw new Error("Unable to identify patient for update");
          }
        }

        // Ensure we have a valid patientId before proceeding
        if (!patientId) {
          throw new Error("No patient ID available - unable to save order");
        }

        // Prepare order data with all facility manipulation fields
        const orderData = {
          patient_id: patientId,
          creator_id: user?.id,
          order_status: 'unapproved' as const,
          diagnosis: formData.diagnosisStage,
          
          // Donor Information
          donor_not_applicable: formData.donorNotApplicable,
          donor_first_name: formData.donorFirstName,
          donor_last_name: formData.donorLastName,
          donor_middle_name: formData.donorMiddleName,
          donor_gender: formData.donorGender,
          donor_weight_kg: parseFloat(formData.donorWeight) || null,
          donor_dob: formData.donorDob || null,
          donor_mrn: formData.donorMrn,
          donor_abo_rh: formData.donorAboRh,
          
          // Processing Requirements
          collection_date: formData.collectionDate || null,
          processing_within_24h: formData.processingWithin24h,
          processing_within_48h: formData.processingWithin48h,
          hpc_apheresis_procedures: formData.hpcApheresisProc,
          tc_t_cells_procedures: formData.tcTCellsProc,
          hpc_marrow_procedures: formData.hpcMarrowProc,
          hpc_cord_blood_procedures: formData.hpcCordBloodProc,
          common_assays_procedures: formData.commonAssaysProc,
          
          // Packaging
          special_aliquoting: formData.specialAliquoting,
          cd34_dose_option: formData.cd34DoseOption,
          cd34_split_evenly: formData.cd34SplitEvenly,
          cd34_min_dose: parseFloat(formData.cd34MinDose) || null,
          cd34_max_dose: parseFloat(formData.cd34MaxDose) || null,
          cd34_bag_doses: formData.cd34BagDoses,
          cd3_dose_option: formData.cd3DoseOption,
          cd3_split_evenly: formData.cd3SplitEvenly,
          cd3_bag_doses: formData.cd3BagDoses,
          additional_packaging_instructions: formData.additionalPackagingInstructions,
          
          // Miscellaneous
          intended_tnc_dose: parseFloat(formData.intendedTncDose) || null,
          intended_cd34_dose: parseFloat(formData.intendedCd34Dose) || null,
          intended_cd3_dose: parseFloat(formData.intendedCd3Dose) || null,
          abo_rh_testing_option: formData.aboRhTestingOption,
          physician_approval: formData.physicianApproval,
          // Missing fields
          cell_dose: parseFloat(formData.cellDose) || null,
          cell_dose_unit: formData.cellDoseUnit,
          infusion_date: formData.infusionDate || null,
          treatment_type: formData.treatmentType,
          special_instructions: formData.specialInstructions,
        };

        if (editOrder) {
          await api.updateOrder(editOrder.id, orderData);
          toast({
            title: "Order Updated Successfully",
            description: `Facility manipulation order for ${formData.firstName} ${formData.lastName} has been updated.`,
          });
        } else {
          await api.createOrder(orderData);
          toast({
            title: "Order Created Successfully",
            description: `Facility manipulation order for ${formData.firstName} ${formData.lastName} has been created.`,
          });
        }
      }
      
      onComplete();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: isReviewMode ? "Failed to approve order. Please try again." : "Failed to process order. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mrn">Medical Record # (7 digits) *</Label>
              <Input
                id="mrn"
                value={formData.mrn}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').substring(0, 7);
                  handleInputChange('mrn', value);
                  searchPatientByMRN(value);
                }}
                placeholder="1234567"
                maxLength={7}
                className={formData.mrn && formData.mrn.length !== 7 ? "border-red-500" : ""}
                disabled={shouldDisableFields()}
              />
              {formData.mrn && formData.mrn.length !== 7 && (
                <p className="text-sm text-red-600">
                  MRN must be exactly 7 digits. Current: {formData.mrn.length} digit{formData.mrn.length !== 1 ? 's' : ''}
                </p>
              )}
              {existingPatient && !editOrder && (
                <Badge variant="secondary" className="text-sm">
                  Existing patient found
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="middleName">Middle Name</Label>
                <Input
                  id="middleName"
                  value={formData.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  disabled={shouldDisableFields()}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sex *</Label>
                <RadioGroup 
                  value={formData.gender} 
                  onValueChange={(value) => handleInputChange('gender', value)}
                  disabled={!!existingPatient || shouldDisableFields()}
                  className="flex space-x-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="male" id="male" />
                    <Label htmlFor="male">Male</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="female" id="female" />
                    <Label htmlFor="female">Female</Label>
                  </div>
                </RadioGroup>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="weight">Patient Weight *</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    id="weight"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', e.target.value)}
                    placeholder="80"
                    disabled={shouldDisableFields()}
                  />
                  <span className="text-sm text-gray-500">kg</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">DOB *</Label>
                <Input
                  id="dateOfBirth"
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2 relative">
                <Label htmlFor="diagnosisStage">Diagnosis/Stage *</Label>
                <Input
                  id="diagnosisStage"
                  value={formData.diagnosisStage}
                  onChange={(e) => {
                    handleInputChange('diagnosisStage', e.target.value);
                    searchICD(e.target.value);
                  }}
                  onFocus={() => {
                    if (diagnosisSuggestions.length > 0) {
                      setShowDiagnosisSuggestions(true);
                    }
                  }}
                  onBlur={() => {
                    // Delay hiding suggestions to allow clicking
                    setTimeout(() => setShowDiagnosisSuggestions(false), 200);
                  }}
                  placeholder="Enter diagnosis (e.g., Acute myeloid leukemia)"
                disabled={shouldDisableFields()}
                />
                
                {showDiagnosisSuggestions && diagnosisSuggestions.length > 0 && (
                  <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {diagnosisSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 text-sm"
                        onMouseDown={() => {
                          // Only store the diagnosis name/description, not the code
                          const displayText = suggestion.description || suggestion;
                          handleInputChange('diagnosisStage', displayText);
                          setShowDiagnosisSuggestions(false);
                        }}
                      >
                        <div className="text-gray-900 font-medium">
                          {suggestion.description || suggestion}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="aboRh">ABO/Rh</Label>
                <Select 
                  value={formData.aboRh} 
                  onValueChange={(value) => handleInputChange('aboRh', value)}
                  disabled={shouldDisableFields()}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent>
                    {bloodGroups.map((group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="ssn">SSN</Label>
                <Input
                  id="ssn"
                  value={formData.ssn}
                  onChange={(e) => handleInputChange('ssn', e.target.value)}
                disabled={shouldDisableFields()}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contactPerson">Contact Person *</Label>
                <Input
                  id="contactPerson"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  placeholder="Patterson"
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="physician">Physician *</Label>
                <Input
                  id="physician"
                  value={formData.physician}
                  onChange={(e) => handleInputChange('physician', e.target.value)}
                  placeholder="Dr. Rowley"
                  disabled={shouldDisableFields()}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phoneNumber">Phone # *</Label>
                <Input
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => {
                  // Allow only numbers, spaces, hyphens, and parentheses
                  const value = e.target.value.replace(/[^\d\s\-\(\)]/g, '');
                  handleInputChange('phoneNumber', value);
                }}
                placeholder="201-234-0938"
                disabled={shouldDisableFields()}
              />
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="donorNotApplicable"
                checked={formData.donorNotApplicable}
                onCheckedChange={(checked) => handleInputChange('donorNotApplicable', !!checked)}
                disabled={shouldDisableFields()}
              />
              <Label htmlFor="donorNotApplicable" className="text-lg font-medium">
                This Section is Not Applicable
              </Label>
            </div>
            
            {!formData.donorNotApplicable && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="donorLastName">Last Name</Label>
                    <Input
                      id="donorLastName"
                      value={formData.donorLastName}
                      onChange={(e) => handleInputChange('donorLastName', e.target.value)}
                      disabled={shouldDisableFields()}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="donorFirstName">First Name</Label>
                    <Input
                      id="donorFirstName"
                      value={formData.donorFirstName}
                      onChange={(e) => handleInputChange('donorFirstName', e.target.value)}
                      disabled={shouldDisableFields()}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="donorMiddleName">Middle Name</Label>
                    <Input
                      id="donorMiddleName"
                      value={formData.donorMiddleName}
                      onChange={(e) => handleInputChange('donorMiddleName', e.target.value)}
                      disabled={shouldDisableFields()}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sex</Label>
                    <RadioGroup 
                      value={formData.donorGender} 
                      onValueChange={(value) => handleInputChange('donorGender', value)}
                      className="flex space-x-6"
                      disabled={shouldDisableFields()}
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="male" id="donorMale" />
                        <Label htmlFor="donorMale">Male</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="female" id="donorFemale" />
                        <Label htmlFor="donorFemale">Female</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="donorWeight">Patient Weight</Label>
                    <Input
                      id="donorWeight"
                      value={formData.donorWeight}
                      onChange={(e) => handleInputChange('donorWeight', e.target.value)}
                      placeholder="75"
                      disabled={shouldDisableFields()}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="donorDob">DOB</Label>
                    <Input
                      id="donorDob"
                      type="date"
                      value={formData.donorDob}
                      onChange={(e) => handleInputChange('donorDob', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="donorMrn">Medical Record #</Label>
                    <Input
                      id="donorMrn"
                      value={formData.donorMrn}
                      onChange={(e) => handleInputChange('donorMrn', e.target.value)}
                      disabled={shouldDisableFields()}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="donorAboRh">ABO/Rh</Label>
                  <Select 
                    value={formData.donorAboRh} 
                    onValueChange={(value) => handleInputChange('donorAboRh', value)}
                    disabled={shouldDisableFields()}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select blood group" />
                    </SelectTrigger>
                    <SelectContent>
                      {bloodGroups.map((group) => (
                        <SelectItem key={group} value={group}>
                          {group}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="collectionDate">Approximate date of initial collection/harvest</Label>
              <Input
                id="collectionDate"
                type="date"
                value={formData.collectionDate}
                onChange={(e) => handleInputChange('collectionDate', e.target.value)}
                disabled={shouldDisableFields()}
              />
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="processingWithin24h"
                  checked={formData.processingWithin24h}
                  onCheckedChange={(checked) => handleInputChange('processingWithin24h', !!checked)}
                  disabled={shouldDisableFields()}
                />
                <Label htmlFor="processingWithin24h">
                  Anticipated date of processing within 24 hours of collection
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="processingWithin48h"
                  checked={formData.processingWithin48h}
                  onCheckedChange={(checked) => handleInputChange('processingWithin48h', !!checked)}
                  disabled={shouldDisableFields()}
                />
                <Label htmlFor="processingWithin48h">
                  Anticipated date of processing within 48 hrs of collection (international collections)
                </Label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="font-semibold">HPC-Apheresis</h4>
                {['Accession and Evaluation (P-101)', 'Cryopreservation (P-102)', 'CD34 Selection (P-103)', 'T Cell Depletion (P-105)', 'T Cell Depletion/Cryopres (P-106)'].map((proc, index) => (
                  <div key={proc} className="flex items-center space-x-2">
                    <Checkbox 
                      id={createValidId(proc, 'hpc-apheresis', index)}
                      checked={formData.hpcApheresisProc.includes(proc)}
                      onCheckedChange={(checked) => handleArrayChange('hpcApheresisProc', proc, !!checked)}
                    />
                    <Label htmlFor={createValidId(proc, 'hpc-apheresis', index)} className="text-sm">{proc}</Label>
                  </div>
                ))}
                
                <h4 className="font-semibold mt-6">HPC-Marrow</h4>
                {['Accession and Evaluation (P-201)', 'Cryopreservation (P-202)', 'CD34 Selection (P-203)', 'CD34 Selection/Cryopres (P-204)', 'T Cell Depletion (P-205)', 'T Cell Depletion/Cryopres (P-206)', 'Plasma Depletion (P-208)', 'RBC Depletion (P-209)'].map((proc, index) => (
                  <div key={proc} className="flex items-center space-x-2">
                    <Checkbox 
                      id={createValidId(proc, 'hpc-marrow', index)}
                      checked={formData.hpcMarrowProc.includes(proc)}
                      onCheckedChange={(checked) => handleArrayChange('hpcMarrowProc', proc, !!checked)}
                    />
                    <Label htmlFor={createValidId(proc, 'hpc-marrow', index)} className="text-sm">{proc}</Label>
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <h4 className="font-semibold">TC-T Cells (Donor Leukocytes)</h4>
                {['Accession and Evaluation (P-301)', 'Cryopreservation (P-302)', 'CD34 Selection/Cryopres (P-104)'].map((proc, index) => (
                  <div key={proc} className="flex items-center space-x-2">
                    <Checkbox 
                      id={createValidId(proc, 'tc-t-cells', index)}
                      checked={formData.tcTCellsProc.includes(proc)}
                      onCheckedChange={(checked) => handleArrayChange('tcTCellsProc', proc, !!checked)}
                    />
                    <Label htmlFor={createValidId(proc, 'tc-t-cells', index)} className="text-sm">{proc}</Label>
                  </div>
                ))}
                
                <h4 className="font-semibold mt-6">HPC-Cord Blood (Cord Blood)</h4>
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id={createValidId('Thaw/prep for infusion (P-407)', 'hpc-cord-blood', 0)}
                    checked={formData.hpcCordBloodProc.includes('Thaw/prep for infusion (P-407)')}
                    onCheckedChange={(checked) => handleArrayChange('hpcCordBloodProc', 'Thaw/prep for infusion (P-407)', !!checked)}
                  />
                  <Label htmlFor={createValidId('Thaw/prep for infusion (P-407)', 'hpc-cord-blood', 0)} className="text-sm">Thaw/prep for infusion (P-407)</Label>
                </div>
                
                <h4 className="font-semibold mt-6">Common Assays (if not in P code)</h4>
                {['CD3 Enumeration (A-203)', 'ABO/Rh Testing (A-801)', '7-AAD Viability Testing (A-204)', 'CD34 Enumeration (A-201)', 'CBC Enumeration (A-206)', 'CFU Enumeration (A-101)'].map((proc, index) => (
                  <div key={proc} className="flex items-center space-x-2">
                    <Checkbox 
                      id={createValidId(proc, 'common-assays', index)}
                      checked={formData.commonAssaysProc.includes(proc)}
                      onCheckedChange={(checked) => handleArrayChange('commonAssaysProc', proc, !!checked)}
                    />
                    <Label htmlFor={createValidId(proc, 'common-assays', index)} className="text-sm">{proc}</Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
        
      case 4:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <Label className="text-lg font-medium">Do you have special requirements for aliquoting final product?</Label>
              <RadioGroup 
                value={formData.specialAliquoting.toString()} 
                onValueChange={(value) => handleInputChange('specialAliquoting', value === 'true')}
                className="flex space-x-6"
                disabled={shouldDisableFields()}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="true" id="aliquotYes" />
                  <Label htmlFor="aliquotYes">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="false" id="aliquotNo" />
                  <Label htmlFor="aliquotNo">No</Label>
                </div>
              </RadioGroup>
            </div>
            
            {formData.specialAliquoting && (
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="cd34DoseEnabled"
                      checked={!!formData.cd34DoseOption}
                      onCheckedChange={(checked) => handleInputChange('cd34DoseOption', checked ? 'enabled' : '')}
                    />
                    <Label htmlFor="cd34DoseEnabled" className="font-medium">CD34 dose in each container</Label>
                  </div>
                  
                  {formData.cd34DoseOption && (
                    <div className="ml-6 space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="cd34SplitEvenly"
                          checked={formData.cd34SplitEvenly}
                          onCheckedChange={(checked) => handleInputChange('cd34SplitEvenly', !!checked)}
                        />
                        <Label htmlFor="cd34SplitEvenly">Split evenly</Label>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="cd34MinDose">Minimum dose/container</Label>
                          <div className="flex items-center space-x-2">
                            <Input
                              id="cd34MinDose"
                              value={formData.cd34MinDose}
                              onChange={(e) => handleInputChange('cd34MinDose', e.target.value)}
                              className="w-24"
                            />
                            <span className="text-sm">x 10⁶/kg</span>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="cd34MaxDose">Maximum dose/container</Label>
                          <div className="flex items-center space-x-2">
                            <Input
                              id="cd34MaxDose"
                              value={formData.cd34MaxDose}
                              onChange={(e) => handleInputChange('cd34MaxDose', e.target.value)}
                              className="w-24"
                            />
                            <span className="text-sm">x 10⁶/kg</span>
                          </div>
                        </div>
                      </div>
                      
                        <div className="space-y-2">
                        <Label>Specific dose in each container (x 10⁶/kg)</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {['Bag A', 'Bag B', 'Bag C', 'Bag D'].map((bag, index) => (
                            <div key={bag} className="space-y-1">
                              <Label className="text-sm" htmlFor={`cd34-${createValidId(bag, 'dose', index)}`}>{bag}</Label>
                              <Input
                                id={`cd34-${createValidId(bag, 'dose', index)}`}
                                value={formData.cd34BagDoses[bag] || ''}
                                onChange={(e) => handleBagDoseChange('cd34BagDoses', {...formData.cd34BagDoses, [bag]: e.target.value})}
                                className="w-20"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="cd3DoseEnabled"
                      checked={!!formData.cd3DoseOption}
                      onCheckedChange={(checked) => handleInputChange('cd3DoseOption', checked ? 'enabled' : '')}
                    />
                    <Label htmlFor="cd3DoseEnabled" className="font-medium">CD3 dose in each container</Label>
                  </div>
                  
                  {formData.cd3DoseOption && (
                    <div className="ml-6 space-y-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="cd3SplitEvenly"
                          checked={formData.cd3SplitEvenly}
                          onCheckedChange={(checked) => handleInputChange('cd3SplitEvenly', !!checked)}
                        />
                        <Label htmlFor="cd3SplitEvenly">Split evenly</Label>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Specific dose in each container (x 10⁷/kg)</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {['Bag A', 'Bag B', 'Bag C', 'Bag D', 'Bag E', 'Bag F'].map((bag, index) => (
                            <div key={bag} className="space-y-1">
                              <Label className="text-sm" htmlFor={`cd3-${createValidId(bag, 'dose', index)}`}>{bag}</Label>
                              <Input
                                id={`cd3-${createValidId(bag, 'dose', index)}`}
                                value={formData.cd3BagDoses[bag] || ''}
                                onChange={(e) => handleBagDoseChange('cd3BagDoses', {...formData.cd3BagDoses, [bag]: e.target.value})}
                                className="w-20"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="additionalPackagingInstructions">Additional Packaging Instructions</Label>
              <Textarea
                id="additionalPackagingInstructions"
                value={formData.additionalPackagingInstructions}
                onChange={(e) => handleInputChange('additionalPackagingInstructions', e.target.value)}
                rows={4}
                disabled={shouldDisableFields()}
              />
            </div>
          </div>
        );
        
      case 5:
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-lg font-semibold">Collection Goals</h4>
              
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intendedTncDose">Intended TNC Dose</Label>
                <Input
                  id="intendedTncDose"
                  value={formData.intendedTncDose}
                  onChange={(e) => handleInputChange('intendedTncDose', e.target.value)}
                  placeholder="Enter dose"
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="intendedCd34Dose">Intended CD34+ Dose</Label>
                <Input
                  id="intendedCd34Dose"
                  value={formData.intendedCd34Dose}
                  onChange={(e) => handleInputChange('intendedCd34Dose', e.target.value)}
                  placeholder="Enter dose"
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="intendedCd3Dose">Intended CD3+ Dose</Label>
                <Input
                  id="intendedCd3Dose"
                  value={formData.intendedCd3Dose}
                  onChange={(e) => handleInputChange('intendedCd3Dose', e.target.value)}
                  placeholder="Enter dose"
                  disabled={shouldDisableFields()}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cellDose">Cell Dose</Label>
                <Input
                  id="cellDose"
                  value={formData.cellDose}
                  onChange={(e) => handleInputChange('cellDose', e.target.value)}
                  placeholder="Enter cell dose"
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cellDoseUnit">Cell Dose Unit</Label>
                <Select 
                  value={formData.cellDoseUnit} 
                  onValueChange={(value) => handleInputChange('cellDoseUnit', value)}
                  disabled={shouldDisableFields()}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cells/kg">cells/kg</SelectItem>
                    <SelectItem value="cells/m²">cells/m²</SelectItem>
                    <SelectItem value="x10⁶ cells">x10⁶ cells</SelectItem>
                    <SelectItem value="x10⁹ cells">x10⁹ cells</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="infusionDate">Infusion Date</Label>
                <Input
                  id="infusionDate"
                  type="date"
                  value={formData.infusionDate}
                  onChange={(e) => handleInputChange('infusionDate', e.target.value)}
                  disabled={shouldDisableFields()}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="treatmentType">Treatment Type</Label>
                <Select 
                  value={formData.treatmentType} 
                  onValueChange={(value) => handleInputChange('treatmentType', value)}
                  disabled={shouldDisableFields()}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select treatment type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Autologous Stem Cell Transplant">Autologous Stem Cell Transplant</SelectItem>
                    <SelectItem value="Allogeneic Stem Cell Transplant">Allogeneic Stem Cell Transplant</SelectItem>
                    <SelectItem value="CAR-T Cell Therapy">CAR-T Cell Therapy</SelectItem>
                    <SelectItem value="Donor Lymphocyte Infusion">Donor Lymphocyte Infusion</SelectItem>
                    <SelectItem value="Gene Therapy">Gene Therapy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="specialInstructions">Special Instructions</Label>
              <Textarea
                id="specialInstructions"
                value={formData.specialInstructions}
                onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                placeholder="Enter any special instructions or notes"
                disabled={shouldDisableFields()}
                rows={3}
              />
            </div>
            </div>
            
             <div className="space-y-4">
               <h4 className="text-lg font-semibold">ABO/Rh Determination (Check One)</h4>
               
               <RadioGroup 
                 value={formData.aboRhTestingOption} 
                 onValueChange={(value) => handleInputChange('aboRhTestingOption', value)}
                 className="space-y-2"
                 disabled={shouldDisableFields()}
               >
                 <div className="flex items-center space-x-2">
                   <RadioGroupItem value="Patient Blood Only" id="patient-blood-only" />
                   <Label htmlFor="patient-blood-only">Patient Blood Only</Label>
                 </div>
                 <div className="flex items-center space-x-2">
                   <RadioGroupItem value="Donor Blood Only" id="donor-blood-only" />
                   <Label htmlFor="donor-blood-only">Donor Blood Only</Label>
                 </div>
                 <div className="flex items-center space-x-2">
                   <RadioGroupItem value="Patient and Donor Blood" id="patient-donor-blood" />
                   <Label htmlFor="patient-donor-blood">Patient and Donor Blood</Label>
                 </div>
               </RadioGroup>
             </div>
            
            {/* Show approval information if order is already approved */}
            {editOrder?.approved_by && editOrder?.approved_at && (
              <div className="space-y-2 p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="text-lg font-semibold text-green-800">Approval Information</h4>
                <div className="text-sm text-green-700">
                  <p><strong>Approved by:</strong> {editOrder.approved_by}</p>
                  <p><strong>Approved on:</strong> {new Date(editOrder.approved_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</p>
                </div>
              </div>
            )}

            {isReviewMode && user?.role === 'physician' && !editOrder?.approved_by && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="physicianApproval"
                    checked={formData.physicianApproval}
                    onCheckedChange={(checked) => handleInputChange('physicianApproval', !!checked)}
                  />
                  <Label htmlFor="physicianApproval" className="text-lg font-semibold">
                    I Approve
                  </Label>
                </div>
                <p className="text-sm text-blue-700">
                  Check this box to approve the facility manipulation order and send it to PCT.
                </p>
              </div>
            )}
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-4xl h-[85vh] overflow-hidden flex flex-col"
        aria-describedby="order-form-description"
      >
        {/* Fixed Header */}
        <DialogHeader className="flex-shrink-0 pb-4 relative">
          {isReviewMode && (
            <div className="absolute top-0 right-0 text-xs text-muted-foreground">
              Read-only mode
            </div>
          )}
          <DialogTitle className="text-2xl font-bold text-medical-blue">
            {isReviewMode ? 'Review & Approve Facility Manipulation Order' : editOrder ? 'Edit Facility Manipulation Order' : 'Facility Manipulation Order Form'}
            {/* Show patient info when available */}
            {(formData.firstName && formData.lastName && formData.mrn) && (
              <span className="text-lg font-normal text-gray-600 ml-3">
                for {formData.firstName} {formData.lastName} (MRN: {formData.mrn})
              </span>
            )}
          </DialogTitle>
          <div id="order-form-description" className="sr-only">
            {isReviewMode ? 'Review and approve facility manipulation order details' : editOrder ? 'Edit existing facility manipulation order' : 'Create a new facility manipulation order for patient treatment'}
          </div>
        </DialogHeader>
        
        {/* Fixed Progress Steps */}
        <div className="flex-shrink-0 px-4 pb-6">
          <div className="flex items-start relative w-full">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isCompleted = currentStep > step.number;
              const isCurrent = currentStep === step.number;
              const isValid = validateStep(step.number);
              const shouldShowValid = (step.number === currentStep && isValid) || isCompleted;
              const allStepsValid = areAllStepsValid();
              const isClickable = allStepsValid && step.number !== currentStep;
              
              // Special case for physician approval in review mode
              const showPhysicianApprovalValid = isReviewMode && user?.role === 'physician' && 
                step.number === 5 && currentStep === 5 && formData.physicianApproval;
              
              const finalShouldShowValid = shouldShowValid || showPhysicianApprovalValid;
              
              return (
                <div key={step.number} className="flex flex-col items-center flex-1 relative">
                  <div 
                    className={`
                      w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300 z-10 bg-white relative
                      ${isCurrent ? 'border-medical-blue' : 'border-gray-300'}
                      ${isClickable ? 'cursor-pointer hover:scale-110 hover:shadow-lg' : ''}
                    `}
                    onClick={() => isClickable && navigateToStep(step.number)}
                    title={isClickable ? `Go to ${step.title}` : ''}
                  >
                    <StepIcon className={`h-6 w-6 transition-colors duration-300 ${
                      isCurrent ? 'text-medical-blue' : 'text-gray-400'
                    }`} />
                    {/* Green overlay for completed/valid steps */}
                    {finalShouldShowValid && (
                      <div className="absolute inset-0 bg-green-500 rounded-full opacity-80 flex items-center justify-center">
                        <StepIcon className="h-6 w-6 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="text-center mt-2">
                    <p className={`text-sm font-medium ${
                      isCompleted || finalShouldShowValid ? 'text-green-600' :
                      isCurrent ? 'text-medical-blue' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-400 max-w-20 leading-tight">
                      {step.description}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`absolute top-6 left-1/2 h-0.5 w-full ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-300'
                    }`} style={{ transform: 'translateX(-50%)', width: 'calc(100vw / 5)' }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Flexible Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Validation Error Display - Above Content */}
          {validationError && (
            <div className="flex-shrink-0 px-4 pb-2">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {validationError}
                </AlertDescription>
              </Alert>
            </div>
          )}

          <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <CardHeader className="flex-shrink-0 border-b border-gray-200 pb-4">
              <CardTitle className="flex items-center space-x-2">
                {steps[currentStep - 1] && (
                  <>
                    {(() => {
                      const StepIcon = steps[currentStep - 1].icon;
                      return <StepIcon className="h-5 w-5 text-medical-blue" />;
                    })()}
                    <span>{steps[currentStep - 1].title}</span>
                  </>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-6 min-h-0">
              {renderStepContent()}
            </CardContent>
          </Card>
        </div>
        
        {/* Fixed Footer Navigation */}
        <div className="flex-shrink-0 flex justify-between px-4 pt-6 pb-4 border-t bg-background">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="flex items-center space-x-2"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Previous</span>
          </Button>
          
          {currentStep < 5 ? (
            <Button
              onClick={nextStep}
              className={`flex items-center space-x-2 transition-colors ${
                validateStep(currentStep) 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gradient-primary'
              }`}
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`${
                (isReviewMode && user?.role === 'physician' && formData.physicianApproval) || 
                (editOrder && areAllStepsValid()) 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gradient-primary'
              }`}
            >
              {isSubmitting 
                ? (isReviewMode ? 'Approving Order...' : editOrder ? 'Updating Order...' : 'Creating Order...') 
                : (isReviewMode && user?.role === 'physician' ? 'APPROVE ORDER' : editOrder ? 'Update Order' : 'Create Order')
              }
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}